import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { GraphQLFormattedError } from 'graphql';
import Form, { FormRef } from '@lib/components/ReactHookForm/FormContainer';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import ActionsRow from '@lib/components/ActionsRow/ActionsRow';
import formStyles from '@lib/assets/styles/Form.module.scss';
import { AccessComponentEnum, AccessPolicyEnum, User } from 'graphql-common';
import { Values } from '@lib/interfaces/form';
import FieldNames from '@lib/enums/fieldNames/userFieldNames';
import {
  getAccessCategoriesScopeIds,
  getAccessesValues,
  getEmptyAccessesValues,
} from '@lib/utils/accessesManagement';
import useSearchListQueries from '../utils/useSearchListQueries';
import getUserAccessesFormFields from '../utils/getUserAccessesFormFields';

type Props = {
  user: User;
  onSubmit: (values: Values) => void;
  onCancel: () => void;
  graphQLErrors?: readonly GraphQLFormattedError[];
};

export default function UserAccessesForm(props: Props) {
  const { user, onSubmit, graphQLErrors, onCancel } = props;
  const { personalAccesses, team } = user;
  const { accesses: teamAccesses } = team;
  const { t } = useTranslation();
  const formRef = useRef<FormRef>(undefined);
  const useSearchListQueriesResult = useSearchListQueries();

  const teamAccessesValues = getAccessesValues(teamAccesses);
  const personalAccessesValues = getAccessesValues(
    personalAccesses,
    AccessPolicyEnum.User,
  );
  const defaultValues = {
    [FieldNames.Accesses]: {
      ...getEmptyAccessesValues(),
      ...teamAccessesValues,
      ...personalAccessesValues,
    },
    [FieldNames.TeamAccesses]: {
      ...getEmptyAccessesValues(),
      ...teamAccessesValues,
    },
    [FieldNames.AccessesMessage]: null,
  };

  const personalInterventionCategoriesIds = getAccessCategoriesScopeIds(
    personalAccesses,
    AccessComponentEnum.InterventionsManagementCategoriesScope,
  );
  const teamInterventionCategoriesIds = getAccessCategoriesScopeIds(
    teamAccesses,
    AccessComponentEnum.InterventionsManagementCategoriesScope,
  );
  const personalAssetCategoriesIds = getAccessCategoriesScopeIds(
    personalAccesses,
    AccessComponentEnum.AssetsManagementCategoriesScope,
  );
  const teamAssetCategoriesIds = getAccessCategoriesScopeIds(
    teamAccesses,
    AccessComponentEnum.AssetsManagementCategoriesScope,
  );
  const personalDocumentationCategoriesIds = getAccessCategoriesScopeIds(
    personalAccesses,
    AccessComponentEnum.DocumentationManagementCategoriesScope,
  );
  const teamDocumentationCategoriesIds = getAccessCategoriesScopeIds(
    teamAccesses,
    AccessComponentEnum.DocumentationManagementCategoriesScope,
  );

  const fields = getUserAccessesFormFields({
    personalInterventionCategoriesIds: personalAccessesValues[
      AccessComponentEnum.InterventionsManagementCategoriesScope
    ]?.itemIds
      ? personalInterventionCategoriesIds
      : teamInterventionCategoriesIds,
    teamInterventionCategoriesIds,
    personalAssetCategoriesIds: personalAccessesValues[
      AccessComponentEnum.AssetsManagementCategoriesScope
    ]?.itemIds
      ? personalAssetCategoriesIds
      : teamAssetCategoriesIds,
    teamAssetCategoriesIds,
    personalDocumentationCategoriesIds: personalAccessesValues[
      AccessComponentEnum.DocumentationManagementCategoriesScope
    ]?.itemIds
      ? personalDocumentationCategoriesIds
      : teamDocumentationCategoriesIds,
    teamDocumentationCategoriesIds,
    getAssetCategoriesLoadOptions:
      useSearchListQueriesResult.getAssetCategoriesLoadOptions,
    getDocumentationCategoriesLoadOptions:
      useSearchListQueriesResult.getDocumentationCategoriesLoadOptions,
    getInterventionCategoriesLoadOptions:
      useSearchListQueriesResult.getInterventionCategoriesLoadOptions,
  });

  const actionsComponent = () => (
    <ActionsRow
      className={formStyles.formActions}
      actions={[
        <Button
          onClick={() => onCancel()}
          buttonType={ButtonTypes.secondaryOutlined}
          buttonText={t('cancel')}
          leftIcon="close"
          fullWidth
        />,
        <Button
          buttonType={ButtonTypes.primaryFilled}
          buttonText={t('save-changes')}
          leftIcon="check"
          type="submit"
          fullWidth
        />,
      ]}
    />
  );

  return (
    <Form
      actionsComponent={actionsComponent}
      defaultValues={defaultValues}
      fields={fields}
      formId="team"
      formRef={formRef}
      graphQLErrors={graphQLErrors}
      onSubmit={onSubmit}
    />
  );
}
